import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, ClipboardList, Calendar, MapPin, Users } from 'lucide-react';

export default function ManualStatsEntry() {
  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { teamId, editMode } = location.state || {};

  const [game, setGame] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerStats, setPlayerStats] = useState({});
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Opponent team stats
  const [opponentStats, setOpponentStats] = useState({
    pts: '',
    fga: '',  // FG Attempts
    fgm: '',  // FG Made
    fga_3pt: '',  // 3PT Attempts
    fgm_3pt: '',  // 3PT Made
    fta: '',  // FT Attempts
    ftm: '',  // FT Made
    oreb: '',
    dreb: '',
    ast: '',
    stl: '',
    blk: '',
    to: '',
    pf: '',
    defl: ''
  });

  useEffect(() => {
    fetchGameData();
  }, [gameId]);

  const fetchGameData = async () => {
    try {
      // Fetch game info
      const gameResponse = await api.get(`/games/${gameId}`);
      const gameData = gameResponse.data;
      setGame(gameData);
      
      // Set scores if they exist (edit mode)
      if (gameData.home_score != null) {
        setHomeScore(gameData.home_score.toString());
        setAwayScore(gameData.away_score?.toString() || '');
        setIsEditMode(true);
      }

      // Fetch players for this game
      const playersResponse = await api.get(`/games/${gameId}/manual-roster`);
      setPlayers(playersResponse.data);

      // Initialize stats for each player (empty defaults)
      const initialStats = {};
      playersResponse.data.forEach(player => {
        initialStats[player.player_id] = {
          pts: '',
          fga: '',
          fgm: '',
          fga_3pt: '',
          fgm_3pt: '',
          fta: '',
          ftm: '',
          oreb: '',
          dreb: '',
          ast: '',
          stl: '',
          blk: '',
          defl: '',
          to: '',
          pf: ''
        };
      });

      // If editing, fetch existing box scores
      if (editMode || gameData.status === 'final') {
        try {
          const boxScoreResponse = await api.get(`/games/${gameId}/box-scores-raw`);
          const boxScores = boxScoreResponse.data || [];
          
          boxScores.forEach(bs => {
            if (bs.player_id && initialStats[bs.player_id]) {
              // Player box score
              initialStats[bs.player_id] = {
                pts: bs.pts?.toString() || '',
                fga: bs.fga?.toString() || '',
                fgm: bs.fgm?.toString() || '',
                fga_3pt: bs.three_pa?.toString() || '',
                fgm_3pt: bs.three_pm?.toString() || '',
                fta: bs.fta?.toString() || '',
                ftm: bs.ftm?.toString() || '',
                oreb: bs.oreb?.toString() || '',
                dreb: bs.dreb?.toString() || '',
                ast: bs.ast?.toString() || '',
                stl: bs.stl?.toString() || '',
                blk: bs.blk?.toString() || '',
                defl: bs.defl?.toString() || '',
                to: bs.to?.toString() || '',
                pf: bs.pf?.toString() || ''
              };
            } else if (bs.is_team_total && bs.side !== (gameData.is_home ? 'home' : 'away')) {
              // Opponent team total
              setOpponentStats({
                pts: bs.pts?.toString() || '',
                fga: bs.fga?.toString() || '',
                fgm: bs.fgm?.toString() || '',
                fga_3pt: bs.three_pa?.toString() || '',
                fgm_3pt: bs.three_pm?.toString() || '',
                fta: bs.fta?.toString() || '',
                ftm: bs.ftm?.toString() || '',
                oreb: bs.oreb?.toString() || '',
                dreb: bs.dreb?.toString() || '',
                ast: bs.ast?.toString() || '',
                stl: bs.stl?.toString() || '',
                blk: bs.blk?.toString() || '',
                defl: bs.defl?.toString() || '',
                to: bs.to?.toString() || '',
                pf: bs.pf?.toString() || ''
              });
            }
          });
          setIsEditMode(true);
        } catch (err) {
          console.log('No existing box scores found');
        }
      }

      setPlayerStats(initialStats);
    } catch (error) {
      toast.error('Failed to load game data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePlayerStat = (playerId, stat, value) => {
    // Only allow numeric values for stats
    if (value !== '' && !/^\d*$/.test(value)) return;
    
    setPlayerStats(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [stat]: value
      }
    }));
  };

  const updateOpponentStat = (stat, value) => {
    // Only allow numeric values for stats
    if (value !== '' && !/^\d*$/.test(value)) return;
    
    setOpponentStats(prev => ({
      ...prev,
      [stat]: value
    }));
  };

  // Calculate shooting percentages for display
  const calculatePercentage = (made, attempts) => {
    const m = parseInt(made) || 0;
    const a = parseInt(attempts) || 0;
    if (a === 0) return '-';
    return Math.round((m / a) * 100) + '%';
  };

  const handleSave = async () => {
    if (!homeScore || !awayScore) {
      toast.error('Please enter the final score');
      return;
    }

    setSaving(true);
    try {
      // Format stats for API - matches box_scores schema for player dashboard compatibility
      const formattedStats = Object.entries(playerStats).map(([playerId, stats]) => ({
        player_id: playerId,
        pts: parseInt(stats.pts) || 0,
        fgm: parseInt(stats.fgm) || 0,
        fga: parseInt(stats.fga) || 0,
        three_pm: parseInt(stats.fgm_3pt) || 0,
        three_pa: parseInt(stats.fga_3pt) || 0,
        ftm: parseInt(stats.ftm) || 0,
        fta: parseInt(stats.fta) || 0,
        oreb: parseInt(stats.oreb) || 0,
        dreb: parseInt(stats.dreb) || 0,
        ast: parseInt(stats.ast) || 0,
        stl: parseInt(stats.stl) || 0,
        blk: parseInt(stats.blk) || 0,
        defl: parseInt(stats.defl) || 0,
        to: parseInt(stats.to) || 0,
        pf: parseInt(stats.pf) || 0
      }));

      // Format opponent stats
      const formattedOpponentStats = {
        pts: parseInt(opponentStats.pts) || 0,
        fgm: parseInt(opponentStats.fgm) || 0,
        fga: parseInt(opponentStats.fga) || 0,
        three_pm: parseInt(opponentStats.fgm_3pt) || 0,
        three_pa: parseInt(opponentStats.fga_3pt) || 0,
        ftm: parseInt(opponentStats.ftm) || 0,
        fta: parseInt(opponentStats.fta) || 0,
        oreb: parseInt(opponentStats.oreb) || 0,
        dreb: parseInt(opponentStats.dreb) || 0,
        ast: parseInt(opponentStats.ast) || 0,
        stl: parseInt(opponentStats.stl) || 0,
        blk: parseInt(opponentStats.blk) || 0,
        defl: parseInt(opponentStats.defl) || 0,
        to: parseInt(opponentStats.to) || 0,
        pf: parseInt(opponentStats.pf) || 0
      };

      await api.post(`/games/${gameId}/manual-stats`, {
        home_score: parseInt(homeScore),
        away_score: parseInt(awayScore),
        player_stats: formattedStats,
        opponent_stats: formattedOpponentStats
      });

      toast.success(isEditMode ? 'Stats updated successfully!' : 'Game summary saved successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save stats');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-12 w-12 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div
      className="h-full overflow-y-auto bg-gradient-to-b from-white to-slate-50"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              data-testid="back-button"
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading uppercase tracking-tight text-slate-900">
                {isEditMode ? 'Edit Game Stats' : 'Manual Stats Entry'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div
        className="max-w-6xl mx-auto p-4 md:p-8 space-y-6 pb-app-nav"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}
      >
        {/* Game Info Banner */}
        {game && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {game.is_home ? 'vs' : '@'} {game.opponent_name}
                </h2>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(game.date_time)}
                  </span>
                  {game.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {game.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manual Entry Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-900">Manual Game Summary</h3>
              <p className="text-sm text-amber-700 mt-1">
                You are entering a manual game summary for this matchup. Enter the final totals for each player below.
              </p>
            </div>
          </div>
        </div>

        {/* Final Score */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900 mb-4">
            Final Score
          </h3>
          <div className="grid grid-cols-2 gap-6 max-w-md">
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                {game?.is_home ? 'Your Team' : 'Opponent'}
              </Label>
              <Input
                type="number"
                min="0"
                value={game?.is_home ? homeScore : awayScore}
                onChange={(e) => game?.is_home ? setHomeScore(e.target.value) : setAwayScore(e.target.value)}
                data-testid="home-score-input"
                className="h-14 text-2xl font-bold text-center"
                placeholder="0"
              />
            </div>
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                {game?.is_home ? 'Opponent' : 'Your Team'}
              </Label>
              <Input
                type="number"
                min="0"
                value={game?.is_home ? awayScore : homeScore}
                onChange={(e) => game?.is_home ? setAwayScore(e.target.value) : setHomeScore(e.target.value)}
                data-testid="away-score-input"
                className="h-14 text-2xl font-bold text-center"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Player Stats Entry */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900">
              Player Stats
            </h3>
            <p className="text-sm text-slate-500 mt-1">Enter final totals for each player</p>
          </div>

          <div className="divide-y divide-slate-100">
            {players.map((player) => (
              <div key={player.player_id} className="p-6" data-testid={`player-stats-${player.player_id}`}>
                {/* Player Header with Points and Percentages */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center font-bold text-white text-lg flex-shrink-0">
                    {player.jersey_number || '-'}
                  </div>
                  <h4 className="font-bold text-slate-900">{player.name}</h4>
                  
                  {/* Calculated Percentages */}
                  <div className="flex items-center gap-3 text-sm ml-auto">
                    <span className="px-2 py-1 bg-slate-100 rounded font-medium">
                      FG: <span className="text-slate-700">{calculatePercentage(playerStats[player.player_id]?.fgm, playerStats[player.player_id]?.fga)}</span>
                    </span>
                    <span className="px-2 py-1 bg-slate-100 rounded font-medium">
                      3PT: <span className="text-slate-700">{calculatePercentage(playerStats[player.player_id]?.fgm_3pt, playerStats[player.player_id]?.fga_3pt)}</span>
                    </span>
                    <span className="px-2 py-1 bg-slate-100 rounded font-medium">
                      FT: <span className="text-slate-700">{calculatePercentage(playerStats[player.player_id]?.ftm, playerStats[player.player_id]?.fta)}</span>
                    </span>
                  </div>
                  
                  {/* Points Input */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Label className="text-sm font-bold uppercase tracking-wider text-slate-700">
                      Points
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.pts || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'pts', e.target.value)}
                      data-testid={`player-points-${player.player_id}`}
                      className="h-12 w-20 text-center text-xl font-bold"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Stats Grid - FG ATT | FG MADE | 3PT ATT | 3PT MADE | FT ATT | FT MADE */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-4">
                  {/* Field Goals - Made first, then Att */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      FG Made
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.fgm || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'fgm', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      FG Att
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.fga || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'fga', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>

                  {/* 3PT Field Goals - Made first, then Att */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      3PT Made
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.fgm_3pt || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'fgm_3pt', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      3PT Att
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.fga_3pt || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'fga_3pt', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>

                  {/* Free Throws - Made first, then Att */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      FT Made
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.ftm || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'ftm', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      FT Att
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.fta || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'fta', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>

                  {/* Rebounds */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Off Reb
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.oreb || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'oreb', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Def Reb
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.dreb || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'dreb', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>

                  {/* Other Stats */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Assists
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.ast || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'ast', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Steals
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.stl || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'stl', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Blocks
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.blk || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'blk', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Deflections
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.defl || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'defl', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Turnovers
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.to || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'to', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                      Fouls
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={playerStats[player.player_id]?.pf || ''}
                      onChange={(e) => updatePlayerStat(player.player_id, 'pf', e.target.value)}
                      className="h-10 text-center"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Opponent Stats Entry */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-3">
              <Users className="w-5 h-5 text-red-600" />
              <div className="flex-grow">
                <h3 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900">
                  Opponent Stats: {game?.opponent_name}
                </h3>
                <p className="text-sm text-slate-500">Enter final totals for the opposing team</p>
              </div>
              {/* Calculated Percentages for Opponent */}
              <div className="flex items-center gap-3 text-sm">
                <span className="px-2 py-1 bg-red-50 rounded font-medium">
                  FG: <span className="text-red-700">{calculatePercentage(opponentStats.fgm, opponentStats.fga)}</span>
                </span>
                <span className="px-2 py-1 bg-red-50 rounded font-medium">
                  3PT: <span className="text-red-700">{calculatePercentage(opponentStats.fgm_3pt, opponentStats.fga_3pt)}</span>
                </span>
                <span className="px-2 py-1 bg-red-50 rounded font-medium">
                  FT: <span className="text-red-700">{calculatePercentage(opponentStats.ftm, opponentStats.fta)}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-4">
              {/* Points */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Points
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.pts}
                  onChange={(e) => updateOpponentStat('pts', e.target.value)}
                  data-testid="opponent-points-input"
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              {/* Field Goals - Made first, then Att */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  FG Made
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.fgm}
                  onChange={(e) => updateOpponentStat('fgm', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  FG Att
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.fga}
                  onChange={(e) => updateOpponentStat('fga', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>

              {/* 3PT Field Goals - Made first, then Att */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  3PT Made
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.fgm_3pt}
                  onChange={(e) => updateOpponentStat('fgm_3pt', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  3PT Att
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.fga_3pt}
                  onChange={(e) => updateOpponentStat('fga_3pt', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>

              {/* Free Throws - Made first, then Att */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  FT Made
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.ftm}
                  onChange={(e) => updateOpponentStat('ftm', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  FT Att
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.fta}
                  onChange={(e) => updateOpponentStat('fta', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>

              {/* Rebounds */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Off Reb
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.oreb}
                  onChange={(e) => updateOpponentStat('oreb', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Def Reb
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.dreb}
                  onChange={(e) => updateOpponentStat('dreb', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>

              {/* Other Stats */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Assists
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.ast}
                  onChange={(e) => updateOpponentStat('ast', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Steals
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.stl}
                  onChange={(e) => updateOpponentStat('stl', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Blocks
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.blk}
                  onChange={(e) => updateOpponentStat('blk', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Deflections
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.defl}
                  onChange={(e) => updateOpponentStat('defl', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Turnovers
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.to}
                  onChange={(e) => updateOpponentStat('to', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1 block">
                  Fouls
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={opponentStats.pf}
                  onChange={(e) => updateOpponentStat('pf', e.target.value)}
                  className="h-10 text-center"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4 pb-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="h-12 px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-manual-stats-button"
            className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold uppercase tracking-wide"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save Game Summary'}
          </Button>
        </div>
      </div>
    </div>
  );
}
